import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";

/** What the reviewer should DO about an advisory, when the UI has a control for it. */
export type AdvisoryAction = "compare-source" | "field-mapping";

export interface AdvisoryCopy {
  /** Short, action-first title. */
  title: string;
  /** One or two plain sentences: what the system is unsure about and what to check. */
  body: string;
  action?: { kind: AdvisoryAction; label: string };
}

/**
 * Reviewer-facing copy per warning kind (#7431). The backend message is written
 * for the audit trail and names internal traits ("section_0.column_roles");
 * the card needs to say what to check and offer the control that does it.
 * Unknown kinds fall back to the backend message so a new kind never renders
 * blank. The rail story (#7432) turns these into named checks.
 */
const num = (details: Record<string, unknown> | undefined, key: string): number | null => {
  const v = details?.[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
};
const str = (details: Record<string, unknown> | undefined, key: string): string | null => {
  const v = details?.[key];
  return typeof v === "string" && v ? v : null;
};
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const describeAdvisory = (warning: {
  kind: string;
  message: string;
  details?: Record<string, unknown>;
}): AdvisoryCopy => {
  const d = warning.details;
  switch (warning.kind) {
    case "layout_determination_ambiguous":
      return {
        title: "Check the layout against the source",
        body: "The extractor could not tell which rows are data, which are totals, or which claimant some rows belong to. Compare a few records with the source file before approving.",
        action: { kind: "compare-source", label: "Compare with source" },
      };
    case "possible_missing_family_deductible": {
      const members = num(d, "total_members");
      const families = num(d, "family_count") ?? 1;
      const who =
        members !== null
          ? `${members} claimants look like ${families === 1 ? "one family" : `${families} families`}`
          : "Some claimants look like a family";
      return {
        title: families === 1 ? "These claimants may be one family" : "Some claimants may be families",
        body: `${who}, but this policy has no family deductible. If they are a family, add a family-deductible endorsement to the policy before approving. Otherwise each claimant is measured against the specific deductible on their own.`,
      };
    }
    case "items_outside_policy_period": {
      // #7436: both windows; the rail replaces this copy with per-reason rows
      // when it has the loaded lines (reviewChecks). This is the whole-set
      // statement from the server's details.
      const out = num(d, "items_out_of_range");
      const paidStart = str(d, "paid_start") ?? str(d, "policy_start_date");
      const paidEnd = str(d, "paid_end") ?? str(d, "policy_end_date");
      const incStart = str(d, "incurred_start");
      const incEnd = str(d, "incurred_end");
      const span = (a: string | null, b: string | null) =>
        a && b ? `${formatDate(a, DATE_FORMATS.DISPLAY_LONG)} – ${formatDate(b, DATE_FORMATS.DISPLAY_LONG)}` : null;
      const windows = [
        incStart && incEnd ? `Incurred ${span(incStart, incEnd)}` : null,
        paidStart && paidEnd ? `Paid ${span(paidStart, paidEnd)}` : null,
      ]
        .filter(Boolean)
        .join(" · ");
      const amount = str(d, "amount_out_of_range");
      const money =
        amount && Number.isFinite(Number(amount))
          ? Number(amount).toLocaleString("en-US", { style: "currency", currency: "USD" })
          : null;
      return {
        title:
          out !== null
            ? `${out} claim ${plural(out, "line is", "lines are")} outside the policy dates`
            : "Claim lines are outside the policy dates",
        body: [
          windows || null,
          money
            ? `${money} won't count toward deductibles. The lines still load.`
            : "These lines load but won't count toward deductibles.",
        ]
          .filter(Boolean)
          .join(" · "),
      };
    }
    case "reimbursement_template_missing_fields":
      return {
        title: "The layout found no reimbursement amounts",
        body: "The matched layout picked up claimants but none of the reimbursement fields, so approving would create claimant records and no reimbursements. Check the field mappings against the file's column labels and re-save the layout.",
        action: { kind: "field-mapping", label: "Open Field Mapping" },
      };
    case "eor_filter_dropped_all_records":
      return {
        title: "Nothing would load from this document",
        body: "Every extracted row was classified as a per-line item of an EOR, so approving would create no reimbursements. If this is not an EOR document, change the Category above and reprocess.",
      };
    case "policy_lookup_failed":
      // !4937 review (Quinton): a FAILED lookup used to look exactly like
      // "client has no policy" — no Policy cell, no deductible, no error.
      return {
        title: "Couldn't look up the client's policy",
        body: "The deductible and policy shown here may be missing. Reload to try again; if it persists, check the client's policies before approving.",
      };
    default:
      return { title: "Advisory", body: warning.message };
  }
};
